import { createContext, useContext, useState, useEffect } from "react";

// Create Context
const ProductsContext = createContext();

// Custom Hook to use the Context
export const useProducts = () => useContext(ProductsContext);

// Provider Component
export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [pendingProducts, setPendingProducts] = useState({});
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const statuses = [
    "detected",
    "suspicious",
    "reported",
    "takenDown",
    "whitelisted",
    "testPurchase",
  ];

  const statusMap = {
    suspicious: "S",
    whitelisted: "W",
    detected: "D",
    reported: "R",
    takenDown: "TD",
    testPurchase: "T",
  };

  // Effect to handle pending products when context categories are created
  useEffect(() => {
    Object.entries(pendingProducts).forEach(([category, productsList]) => {
      if (productsList && productsList.length > 0) {
        if (!products[category]) {
          setProducts((prev) => ({
            ...prev,
            [category]: productsList,
          }));
        } else {
          // Merge pending products with existing ones
          setProducts((prev) => ({
            ...prev,
            [category]: [...prev[category], ...productsList],
          }));
        }
        // Clear the pending products for this category
        setPendingProducts((prev) => ({
          ...prev,
          [category]: [],
        }));
      }
    });
  }, [products, pendingProducts]);

  const categorizeProducts = (products, status) => {
    return { [status]: products };
  };

  const fetchProductsByStatus = async (status) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("PAuser"));
      if (!userInfo) {
        console.error("No user info found");
        return null;
      }

      const response = await fetch(`${backendUrl}/api/products/byStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid: userInfo?.message?.result.id,
          status: statusMap[status] || status,
        }),
      });

      if (!response.ok) {
        console.error(`Error fetching products for status ${status}`);
        return null;
      }

      const json = await response.json();
      return json;
    } catch (error) {
      console.error("Error fetching products:", error);
      return null;
    }
  };

  const updateProducts = async (newProducts, status) => {
    setLoading(true);
    try {
      return new Promise((resolve) => {
        setProducts((prev) => {
          // If we already have products in this category, merge them
          const existingProducts = prev[status] || [];

          const allProducts = [...existingProducts, ...newProducts];

          const updated = {
            ...prev,
            [status]: allProducts,
          };

          console.log("Updated products:", {
            status,
            count: allProducts.length,
            existing: existingProducts.length,
            new: newProducts.length,
          });

          resolve(updated);
          return updated;
        });
      });
    } catch (error) {
      console.error("Error updating products:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const moveProducts = async (fromCategory, productsToMove, toCategory) => {
    setLoading(true);
    try {
      // Ensure productsToMove is an array
      const productsArray = Array.isArray(productsToMove)
        ? productsToMove
        : [productsToMove];

      console.log("Moving products:", {
        from: fromCategory,
        to: toCategory,
        count: productsArray.length,
        products: productsArray,
      });
      // First, ensure the target category exists by creating it and fetching its products
      if (!products[toCategory]) {
        console.log(
          `Category ${toCategory} doesn't exist, fetching from database...`
        );
        const fetchedProducts = await fetchProductsByStatus(toCategory);
        if (fetchedProducts) {
          await updateProducts(fetchedProducts, toCategory);
        } else {
          await updateProducts([], toCategory);
        }
      }

      return new Promise(async (resolve) => {
        setProducts((prev) => {
          const updatedProducts = { ...prev };

          // Remove products from source category if it exists
          if (updatedProducts[fromCategory]) {
            const productIds = new Set(productsArray.map((p) => p.pd_id));
            updatedProducts[fromCategory] = updatedProducts[
              fromCategory
            ].filter((product) => !productIds.has(product.pd_id));
          }
          console.log("From Category:", updatedProducts[fromCategory]);
          // Initialize target category if it doesn't exist (double-check)
          if (!updatedProducts[toCategory]) {
            updatedProducts[toCategory] = [];
          }
          console.log("To Category:", updatedProducts[toCategory]);
          // Prepare products with new status
          const productsWithNewStatus = productsArray.map((product) => ({
            ...product,
            status: toCategory,
            pd_id: product.pd_id || product.psm_id,
          }));
          console.log("Products with new status:", productsWithNewStatus);

          updatedProducts[toCategory] = [
            ...(updatedProducts[toCategory] || []),
            ...productsWithNewStatus,
          ];
          console.log("Updated To Category:", updatedProducts[toCategory]);
          console.log("Updated products state:", {
            fromCategory: updatedProducts[fromCategory]?.length || 0,
            toCategory: updatedProducts[toCategory]?.length || 0,
            moved: productsWithNewStatus.length,
          });

          resolve(updatedProducts);
          return updatedProducts;
        });
      });
    } catch (error) {
      console.error("Error moving products:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Cleanup effect for pendingProducts
  useEffect(() => {
    const cleanup = () => {
      Object.entries(pendingProducts).forEach(([category, productsList]) => {
        if (productsList && productsList.length > 0 && products[category]) {
          setProducts((prev) => {
            const existingProducts = prev[category] || [];
            const allProducts = [...existingProducts, ...productsList];

            return {
              ...prev,
              [category]: allProducts,
            };
          });

          setPendingProducts((prev) => ({
            ...prev,
            [category]: [],
          }));
        }
      });
    };

    cleanup();
  }, [products, pendingProducts]);

  return (
    <ProductsContext.Provider
      value={{
        products,
        loading,
        updateProducts,
        moveProducts,
        fetchProductsByStatus,
        pendingProducts,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};
